import { useCallback, useRef, useState } from "react";

/**
 * Ustun kengliklari — sudrab o'zgartiriladi va saqlanadi.
 *
 * Nega kerak: jadvallarda ustun soni ko'p va ularning mazmuni har
 * xil uzunlikda — birov uchun tovar nomi muhim, boshqasi uchun
 * miqdor. Brauzer avtomatik kengligi ikkalasiga ham mos kelmaydi.
 *
 * Kengliklar BRAUZERDA saqlanadi (`localStorage`): bu bitta
 * xodimning ish joyi sozlamasi, serverga chiqarish esa uni butun
 * ombor uchun umumiy qilib qo'yardi.
 *
 * Birinchi sudrashda BARCHA ustunlarning joriy kengligi o'lchanib
 * qotiriladi: aks holda bitta ustunni kengaytirish qolganlarini
 * brauzer o'z bilganicha siqib, jadval sakrab ketardi.
 */

const PREFIX = "uyda.cols.";
const MIN_WIDTH = 56;

type Widths = Record<string, number>;

function read(key: string): Widths {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return {};
    const result: Widths = {};
    for (const [column, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value === "number" && Number.isFinite(value) && value >= MIN_WIDTH) {
        result[column] = value;
      }
    }
    return result;
  } catch {
    // Shaxsiy oyna yoki o'chirilgan saqlash — kengliklar
    // avtomatik bo'lib qolaveradi, jadval baribir ishlaydi.
    return {};
  }
}

function write(key: string, widths: Widths): void {
  try {
    if (Object.keys(widths).length === 0) {
      localStorage.removeItem(PREFIX + key);
      return;
    }
    localStorage.setItem(PREFIX + key, JSON.stringify(widths));
  } catch {
    /* e'tiborsiz */
  }
}

export interface ColumnSizing {
  widths: Widths;
  /** Sudrashni boshlaydi (`<th>` chekkasidagi dasta). */
  startResize: (column: string, event: React.PointerEvent<HTMLElement>) => void;
  /** Hamma kengliklarni tiklaydi. */
  reset: () => void;
  /** Kengliklar o'zgartirilganmi — «tiklash» tugmasi uchun. */
  resized: boolean;
  /** Sarlavha qatori: birinchi sudrashda kengliklar shundan o'lchanadi. */
  headRow: React.RefObject<HTMLTableRowElement | null>;
}

export function useColumnWidths(key: string, columns: string[]): ColumnSizing {
  const [widths, setWidths] = useState<Widths>(() => read(key));
  // `<th>` elementlari: birinchi sudrashda joriy kengliklarni
  // o'lchash uchun kerak.
  const headRow = useRef<HTMLTableRowElement | null>(null);

  const startResize = useCallback(
    (column: string, event: React.PointerEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const cells = headRow.current?.querySelectorAll<HTMLTableCellElement>("th:not(.select-col)");
      const startX = event.clientX;

      // Joriy kengliklar: saqlanganlari bor bo'lsa ularni olamiz,
      // qolganini o'lchaymiz.
      const base: Widths = { ...widths };
      if (cells) {
        columns.forEach((name, index) => {
          if (base[name] === undefined) {
            base[name] = Math.max(cells[index]?.offsetWidth ?? MIN_WIDTH, MIN_WIDTH);
          }
        });
      }
      const startWidth = base[column] ?? MIN_WIDTH;

      const move = (pointer: PointerEvent) => {
        const next = Math.max(startWidth + (pointer.clientX - startX), MIN_WIDTH);
        setWidths({ ...base, [column]: Math.round(next) });
      };
      const stop = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", stop);
        document.body.classList.remove("col-resizing");
        setWidths((current) => {
          write(key, current);
          return current;
        });
      };

      document.body.classList.add("col-resizing");
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", stop);
    },
    [columns, key, widths],
  );

  const reset = useCallback(() => {
    setWidths({});
    write(key, {});
  }, [key]);

  return {
    widths,
    startResize,
    reset,
    resized: Object.keys(widths).length > 0,
    headRow,
  };
}
